import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Minus, Trash2, CreditCard, Smartphone, Tag, Receipt, Wine, Search, User, Settings, ShoppingCart, X, Check, Printer, Clock, Save, Bell, DollarSign, TrendingUp, RotateCcw, Wallet } from 'lucide-react';
import config from '../config/config';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import CartContent from '../components/PosCart';

// Sound file for stock alert (place in public folder)
const alertSound = new Audio('/alert.mp3');

const ClubPOS = () => {
  // State declarations
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [discount, setDiscount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '', table: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [savedOrders, setSavedOrders] = useState([]);
  const [showSavedOrders, setShowSavedOrders] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isTaxEnabled, setIsTaxEnabled] = useState(false);
  const [taxRate, setTaxRate] = useState(0.16);
  const [stockAlert, setStockAlert] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [cashTendered, setCashTendered] = useState(0);
  const [changeDue, setChangeDue] = useState(0);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [shiftStatus, setShiftStatus] = useState({ active: false, startTime: null, cashInDrawer: 0 });
  const [cardDetails, setCardDetails] = useState({ cardNumber: '', expiry: '', cvv: '' });
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [editModal, setEditModal] = useState(false);

  const API_URL = `${config.API_URL}/api`;
const [formData, setFormData] = useState({
  id: '',
  cost: '',
  sellPrice: '',
  name: '',
  quantity: '',
  status: 'active',
  images: null, // for FileList
});


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

 const handleSubmit = async () => {
  try {
    const token = localStorage.getItem('token');

    const formPayload = new FormData();
    formPayload.append('id', formData.id);
    formPayload.append('cost', formData.cost);
    formPayload.append('sellPrice', formData.sellPrice);
    formPayload.append('name', formData.name);
    formPayload.append('quantity', formData.quantity);
    formPayload.append('status', formData.status);

    // Append multiple images
    if (formData.images) {
      for (let i = 0; i < formData.images.length; i++) {
        formPayload.append('images[]', formData.images[i]);
      }
    }

    await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

    const response = await axios.post(
      `${config.API_URL}/api/updateproducts`,
      formPayload,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        withCredentials: true,
      }
    );

    if (response.data.status == 1) {
      
     try {
      // Optional reload and cleanup
      setIsLoading(true);
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const params = { page, limit: 20 };
        if (selectedCategory !== 'all') {
          params.category_id = selectedCategory;
        }
        alert('Saved product successfully');
        const response = await axios.get(`${API_URL}/products`, { params, headers });
        setProducts(response.data?.data || []);
        setTotalPages(response.data?.meta?.last_page || 1);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else {
          alert('Failed to load products. Please try again.');
          setProducts([]);
        }
      } finally {
        setIsLoading(false);
      }
    }

    setEditModal(false);
    setFormData({ cost: '', sellPrice: '', quantity: '', status: 'active', images: null, name:'' });

  } catch (error) {
    console.error('Error saving product:', error);
    alert('Error saving product. Try again.');
  } finally {
    setIsLoading(false);
  }
};

  // Utility functions (no dependencies)
  const generateOrderId = () => {
    return 'order_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const formatKES = useCallback((amount) => {
    const numericAmount = Number(amount) || 0;
    const formatted = new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(numericAmount);
    return formatted.replace('KSh', 'Ksh');
  }, []);

  // Computed values (depend on state, defined early)
  const subtotal = cart.reduce((sum, item) => {
    const price = Number(item.price) || 0;
    const quantity = Number(item.quantity) || 0;
    return sum + (price * quantity);
  }, 0);

  const couponDiscount = appliedCoupon
    ? appliedCoupon.type === 'percentage'
      ? subtotal * (Number(appliedCoupon.discount) / 100 || 0)
      : Number(appliedCoupon.discount) || 0
    : 0;

  const manualDiscount = subtotal * (Number(discount) / 100 || 0);
  const totalDiscount = couponDiscount + manualDiscount;
  const taxAmount = isTaxEnabled ? (subtotal - totalDiscount) * (Number(taxRate) || 0) : 0;
  const total = subtotal - totalDiscount + taxAmount;

  // Coupon definitions
  const coupons = {
    'CLUB10': { discount: 10, type: 'percentage', description: '10% club member discount' },
    'HAPPY25': { discount: 25, type: 'fixed', description: '$25 off happy hour' },
    'VIP15': { discount: 15, type: 'percentage', description: '15% VIP member discount' },
    'BIRTHDAY20': { discount: 20, type: 'percentage', description: '20% birthday special' },
  };

  // Core functions (ordered by dependency)
  const addToCart = useCallback((product) => {
    if (!product || !product.id || !product.price || !product.stock) {
      alert('Invalid product data');
      return;
    }

    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      const newQuantity = existing ? existing.quantity + 1 : 1;
      
      if (newQuantity > product.stock) {
        setStockAlert(`Cannot add ${product.name || 'item'}. Quantity exceeds stock (${product.stock} available)`);
        return prev;
      }

      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: newQuantity }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  }, []);


  //Check stock
 const checkStock = useCallback((product) => {

  if (!product || !product.id) {
    alert('Invalid product');
    return;
  }

  setFormData((prev) => ({
    ...prev,
    cost: product.cost || '', 
    id: product.id || '',            // make sure it's not undefined
    sellPrice: product.price || '',
     name: product.name || '',
    quantity: product.stock || 0,
    status: product.status || 'active',
  }));

  setEditModal(true);
}, []);


  const updateQuantity = useCallback((id, demand) => {
    setCart(prevCartItems => prevCartItems
      .map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + demand;
          if (newQuantity > item.stock) {
            setStockAlert(`Cannot increase ${item.name || 'item'}. Quantity exceeds stock (${item.stock} available)`);
            return item;
          }
          return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
        }
        return item;
      })
      .filter(item => item !== null)
    );
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  }, []);

  const applyCoupon = useCallback(() => {
    const coupon = coupons[couponCode.toUpperCase()];
    if (coupon) {
      setAppliedCoupon(coupon);
      setCouponCode('');
    } else {
      alert('Invalid coupon code');
    }
  }, [couponCode]);

  const removeCoupon = useCallback(() => {
    setAppliedCoupon(null);
    setCouponCode('');
  }, []);

  const handleBarcodeScan = useCallback((e) => {
    if (e.key === 'Enter' && barcodeInput) {
      const product = products.find(p => p.barcode === barcodeInput);
      if (product) {
        addToCart(product);
      } else {
        alert('Product not found');
      }
      setBarcodeInput('');
    }
  }, [barcodeInput, products, addToCart]);

  const fetchLatestSavedOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/temp-orders`, { headers });
      setSavedOrders(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch saved orders:', error);
      //alert('Failed to load saved orders. Please try again.');
      setSavedOrders([]);
    }
  }, []);

  const printReceipt = useCallback((order = null) => {
    const orderData = order || {
      id: generateOrderId(),
      items: cart,
      customer: customerInfo,
      order: {
        subtotal: Number(subtotal) || 0,
        discount_amount: Number(totalDiscount) || 0,
        tax_amount: Number(taxAmount) || 0,
        total_amount: Number(total) || 0,
        coupon_code: appliedCoupon ? couponCode : null,
        note: orderNote,
        payment_method: paymentMethod,
        cash_tendered: paymentMethod === 'cash' ? Number(cashTendered) || 0 : null,
        change_due: paymentMethod === 'cash' ? Number(changeDue) || 0 : null
      },
      appliedCoupon,
      note: orderNote,
      timestamp: new Date().toISOString(),
      status: 'pending'
    };

    const receiptHtml = `
      <div style="font-family: Arial, sans-serif; padding: 20px; width: 300px; border: 1px solid #ccc;">
        <h2 style="text-align: center; color: #4B0082;">Club Vintique</h2>
        <p style="text-align: center; font-size: 12px; color: #666;">Premium Club Experience</p>
        <p style="text-align: center; font-size: 12px; color: #666;">Receipt #${orderData.id}</p>
        <p style="text-align: center; font-size: 12px; color: #666;">${new Date(orderData.timestamp).toLocaleString()}</p>
        <hr style="border: 1px dashed #ccc; margin: 10px 0;" />
        <h3 style="font-size: 14px; color: #333;">Order Details</h3>
        ${orderData.customer.name ? `<p style="font-size: 12px;">Customer: ${orderData.customer.name}</p>` : ''}
        ${orderData.customer.table ? `<p style="font-size: 12px;">Order: ${orderData.customer.table}</p>` : ''}
        ${orderData.note ? `<p style="font-size: 12px;">Note: ${orderData.note}</p>` : ''}
        <table style="width: 100%; font-size: 12px; margin-top: 10px;">
          <thead>
            <tr style="border-bottom: 1px solid #ccc;">
              <th style="text-align: left;">Item</th>
              <th style="text-align: center;">Qty</th>
              <th style="text-align: right;">Price</th>
            </tr>
          </thead>
          <tbody>
            ${orderData.items.map(item => `
              <tr>
                <td>${item.name || `Product ${item.product_id || 'Unknown'}`}</td>
                <td style="text-align: center;">${Number(item.quantity) || 0}</td>
                <td style="text-align: right;">${formatKES(Number(item.subtotal) || 0)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <hr style="border: 1px dashed #ccc; margin: 10px 0;" />
        <p style="font-size: 12px;">Subtotal: ${formatKES(orderData.order.subtotal)}</p>
        ${orderData.order.discount_amount > 0 ? `<p style="font-size: 12px; color: green;">Discount: -${formatKES(orderData.order.discount_amount)}</p>` : ''}
        ${orderData.order.tax_amount > 0 ? `<p style="font-size: 12px;">Tax (${(Number(taxRate) * 100).toFixed(0)}%): ${formatKES(orderData.order.tax_amount)}</p>` : ''}
        <p style="font-size: 14px; font-weight: bold;">Total: ${formatKES(orderData.order.total_amount)}</p>
        ${orderData.order.payment_method ? `<p style="font-size: 12px;">Payment Method: ${orderData.order.payment_method.charAt(0).toUpperCase() + orderData.order.payment_method.slice(1)}</p>` : ''}
        ${orderData.order.cash_tendered ? `<p style="font-size: 12px;">Cash Tendered: ${formatKES(orderData.order.cash_tendered)}</p>` : ''}
        ${orderData.order.change_due ? `<p style="font-size: 12px;">Change Due: ${formatKES(orderData.order.change_due)}</p>` : ''}
      </div>
    `;

    const element = document.createElement('div');
    element.innerHTML = receiptHtml;
    document.body.appendChild(element);

    html2pdf()
      .set({
        filename: `receipt_${orderData.id}.pdf`,
        margin: [10, 10, 10, 10],
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      })
      .from(element)
      .save()
      .then(() => {
        document.body.removeChild(element);
        alert('Receipt downloaded as PDF!');
      })
      .catch(error => {
        console.error('Failed to generate PDF:', error);
        document.body.removeChild(element);
        alert('Failed to generate PDF. Please try again.');
      });
  }, [cart, customerInfo, appliedCoupon, couponCode, orderNote, paymentMethod, cashTendered, changeDue, subtotal, totalDiscount, taxAmount, total, taxRate, formatKES]);

  const deleteSavedOrder = useCallback(async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.get(`${API_URL}/delete-orders/${orderId}`, { headers });
      setSavedOrders(prev => prev.filter(order => order.id !== orderId));
    } catch (error) {
      console.error('Failed to delete saved order:', error);
      alert('Failed to delete saved order. Please try again.');
    }
  }, []);

  const prepareCartData = useCallback(() => ({
    items: cart.map(item => ({
      product_id: item.id,
      quantity: Number(item.quantity) || 0,
      unit_price: Number(item.price) || 0,
      subtotal: (Number(item.price) || 0) * (Number(item.quantity) || 0)
    })),
    customer: {
      name: customerInfo.name || null,
      phone: customerInfo.phone || null,
      email: customerInfo.email || null,
      table_number: customerInfo.table || null
    },
    order: {
      subtotal: Number(subtotal) || 0,
      discount_amount: Number(totalDiscount) || 0,
      tax_amount: Number(taxAmount) || 0,
      total_amount: Number(total) || 0,
      coupon_code: appliedCoupon ? couponCode : null,
      note: orderNote || null,
      payment_method: paymentMethod
    },
    appliedCoupon,
    note: orderNote,
    timestamp: new Date().toISOString(),
    status: 'pending'
  }), [cart, customerInfo, appliedCoupon, couponCode, orderNote, paymentMethod, subtotal, totalDiscount, taxAmount, total]);

  const preparePaymentData = useCallback(() => ({
    order: {
      total_amount: Number(total) || 0,
      payment_method: paymentMethod,
      customer_phone: paymentMethod === 'mpesa' ? mpesaPhone : customerInfo.phone,
      cash_tendered: paymentMethod === 'cash' ? Number(cashTendered) || 0 : null,
      change_due: paymentMethod === 'cash' ? Number(changeDue) || 0 : null,
      card_details: paymentMethod === 'card' ? cardDetails : null,
      status: 'completed'
    }
  }), [total, paymentMethod, mpesaPhone, customerInfo.phone, cashTendered, changeDue, cardDetails]);

  const saveOrder = useCallback(async () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }

    const cartData = prepareCartData();
    const token = localStorage.getItem('token');

    try {
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];
      const response = await axios.post(
        `${config.API_URL}/api/save-orders`,
        cartData,
        {
          headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
              'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
            },
            withCredentials: true,

        }
      );

      setSavedOrders(prev => [...prev, response.data?.data || cartData]);
      setCart([]);
      setAppliedCoupon(null);
      setDiscount(0);
      setCustomerInfo({ name: '', phone: '', email: '', table: '' });
      setOrderNote('');
      setShowCartModal(false);
      fetchLatestSavedOrders();
      alert('Order saved successfully! Receipt can be printed.');
    } catch (error) {
      console.error('Failed to save order:', error.response?.data || error.message);
      alert('Failed to save order: ' + (error.response?.data?.message || 'Please try again.'));
    }
  }, [cart, prepareCartData, fetchLatestSavedOrders]);

  const loadSavedOrder = useCallback((order) => {
    if (!order?.items || !Array.isArray(order.items)) {
      alert('Invalid order data');
      return;
    }

    const mappedItems = order.items.map(savedItem => {
      const product = products.find(p => p.id === savedItem.product_id || String(p.id) === String(savedItem.product_id));
      if (!product) {
        console.warn(`Product not found for product_id: ${savedItem.product_id}`);
        return {
          id: savedItem.product_id,
          name: `Unknown Item (${savedItem.product_id})`,
          price: savedItem.unit_price,
          quantity: savedItem.quantity,
          stock: 0,
          subtotal: savedItem.subtotal
        };
      }
      return {
        id: savedItem.product_id,
        name: product.name || `Unknown Item (${savedItem.product_id})`,
        price: savedItem.unit_price,
        quantity: savedItem.quantity,
        stock: product.stock || 0,
        subtotal: savedItem.subtotal
      };
    });

    if (mappedItems.length === 0) {
      alert('No valid items found in saved order');
      return;
    }

    setCart(mappedItems);
    setCustomerInfo(order.customer || { name: '', phone: '', email: '', table: '' });
    setAppliedCoupon(order.appliedCoupon || null);
    setOrderNote(order.note || '');
    setShowSavedOrders(false);
    setShowCartModal(true);
  }, [products]);

  const startShift = useCallback(async (initialCash) => {
    const cash = Number(initialCash) || 0;
    if (isNaN(cash) || cash < 0) {
      alert('Invalid initial cash amount');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/cash-register/start`, { initial_cash: cash }, { headers });
      setShiftStatus({
        active: true,
        startTime: new Date().toISOString(),
        cashInDrawer: cash
      });
      alert('Shift started');
    } catch (error) {
      console.error('Failed to start shift:', error);
      alert('Failed to start shift. Please try again.');
    }
  }, []);

  const endShift = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/cash-register/end`, {}, { headers });
      setShiftStatus({ active: false, startTime: null, cashInDrawer: 0 });
      alert('Shift ended');
    } catch (error) {
      console.error('Failed to end shift:', error);
      alert('Failed to end shift. Please try again.');
    }
  }, []);

  const voidTransaction = useCallback(() => {
    if (cart.length === 0) {
      alert('No transaction to clear');
      return;
    }
    setCart([]);
    setAppliedCoupon(null);
    setDiscount(0);
    setCustomerInfo({ name: '', phone: '', email: '', table: '' });
    setOrderNote('');
    setCashTendered(0);
    setChangeDue(0);
    setShowCartModal(false);
    alert('Transaction cleared');
  }, [cart]);

  const handleCashTendered = useCallback((amount) => {
    const tendered = Number(amount) || 0;
    setCashTendered(tendered);
    setChangeDue(tendered >= total ? (tendered - total) : 0);
  }, [total]);

  const processPayment = useCallback(async () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }
    if (paymentMethod === 'cash' && (Number(cashTendered) < total || isNaN(Number(cashTendered)))) {
      alert('Invalid or insufficient cash tendered');
      return;
    }
    if (paymentMethod === 'mpesa' && !mpesaPhone) {
      alert('Please enter an M-Pesa phone number');
      return;
    }
    if (paymentMethod === 'card' && (!cardDetails.cardNumber || !cardDetails.expiry || !cardDetails.cvv)) {
      alert('Please complete all card details');
      return;
    }

    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const cartData = prepareCartData();
      const paymentData = preparePaymentData();

     
       await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];
 
      const orderResponse = await axios.post(
        `${config.API_URL}/api/postorders`,
        cartData,
        {
         headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
              'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
            },
            withCredentials: true,
        }
      );

      const paymentResponse = await axios.post(
        `${config.API_URL}/api/postpayment`,{
          ...paymentData,
       
        order_id: orderResponse.data.order_id
      },
        {
          headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
              'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
            },
            withCredentials: true,
        }
      );

   /**   const orderResponse = await axios.post(`${API_URL}/postorders`, cartData, { headers });
      const paymentResponse = await axios.post(`${API_URL}/postpayment`, {
        ...paymentData,
        order_id: orderResponse.data?.order_id
      }, { headers });**/

      if (paymentMethod === 'cash') {
        console.log('Opening cash drawer');
        try {
          await axios.post(`${API_URL}/cash-register/update`, {
            cash_tendered: Number(cashTendered) || 0
          }, { headers });
        } catch (error) {
          console.error('Failed to update cash register:', error);
        }
        setShiftStatus(prev => ({
          ...prev,
          cashInDrawer: prev.cashInDrawer + (Number(cashTendered) || 0)
        }));
      }

      const newTransaction = {
        id: orderResponse.data.order_id || generateOrderId(),
        ...cartData,
        payment_method: paymentMethod,
        cash_tendered: paymentMethod === 'cash' ? Number(cashTendered) || 0 : null,
        change_due: paymentMethod === 'cash' ? Number(changeDue) || 0 : null,
        card_details: paymentMethod === 'card' ? cardDetails : null,
        mpesa_phone: paymentMethod === 'mpesa' ? mpesaPhone : null,
        timestamp: new Date().toISOString(),
        status: 'completed'
      };

      setTransactions(prev => [...prev, newTransaction]);
      if (cartData.id) {
        await deleteSavedOrder(cartData.id);
      }

      setIsProcessing(false);
      setShowPayment(false);
      setCart([]);
      setAppliedCoupon(null);
      setDiscount(0);
      setCustomerInfo({ name: '', phone: '', email: '', table: '' });
      setOrderNote('');
      setCashTendered(0);
      setChangeDue(0);
      setShowCartModal(false);
      setCardDetails({ cardNumber: '', expiry: '', cvv: '' });
      setMpesaPhone('');
      alert('Payment processed successfully!');
      printReceipt(newTransaction);
    } catch (error) {
      console.error('Payment processing failed:', error);
      setIsProcessing(false);
      alert('Payment processing failed. Please try again.');
    }
  }, [cart, paymentMethod, cashTendered, total, mpesaPhone, cardDetails, prepareCartData, preparePaymentData, printReceipt, deleteSavedOrder]);

  const processRefund = useCallback(async (order) => {
    if (!order?.id || !order.order?.total_amount) {
      alert('Invalid order data for refund');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/refunds`, {
        order_id: order.id,
        total_amount: Number(order.order.total_amount) || 0,
        payment_method: order.order.payment_method
      }, { headers });

      if (order.order.payment_method === 'cash') {
        console.log('Opening cash drawer for refund');
        try {
          await axios.post(`${API_URL}/cash-register/update`, {
            cash_refunded: Number(order.order.total_amount) || 0
          }, { headers });
        } catch (error) {
          console.error('Failed to update cash register for refund:', error);
        }
        setShiftStatus(prev => ({
          ...prev,
          cashInDrawer: prev.cashInDrawer - (Number(order.order.total_amount) || 0)
        }));
      }

      setTransactions(prev => prev.map(t => 
        t.id === order.id ? { ...t, status: 'refunded' } : t
      ));
      await deleteSavedOrder(order.id);
      alert('Refund processed successfully');
    } catch (error) {
      console.error('Refund processing failed:', error);
      alert('Refund processing failed. Please try again.');
    }
  }, [deleteSavedOrder]);

  // Effect hooks
  useEffect(() => {
    const storedTransactions = localStorage.getItem('clubpos_transactions');
    setTransactions(storedTransactions ? JSON.parse(storedTransactions) : []);
  }, []);

  useEffect(() => {
    const fetchSavedOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API_URL}/temp-orders`, { headers });
        setSavedOrders(response.data?.data || []);
      } catch (error) {
        console.error('Failed to fetch saved orders:', error);
        alert('Failed to load saved orders. Please try again.');
        setSavedOrders([]);
      }
    };
    fetchSavedOrders();
  }, []);

  useEffect(() => {
    localStorage.setItem('clubpos_transactions', JSON.stringify(transactions));
  }, [transactions]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (stockAlert) {
      alertSound.play().catch(error => console.error('Audio play failed:', error));
      setTimeout(() => setStockAlert(null), 3000);
    }
  }, [stockAlert]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const categoriesRes = await axios.get(`${API_URL}/categories`, { headers });
        setCategories(categoriesRes.data?.adminCategories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        alert('Failed to load categories. Please try again.');
        setCategories([]);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const params = { page, limit: 20 };
        if (selectedCategory !== 'all') {
          params.category_id = selectedCategory;
        }
        const response = await axios.get(`${API_URL}/products`, { params, headers });
        setProducts(response.data?.data || []);
        setTotalPages(response.data?.meta?.last_page || 1);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else {
          alert('Failed to load products. Please try again.');
          setProducts([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, [page, selectedCategory]);

    const onSignOut = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

   const goToHome = () => {
    const token = localStorage.getItem('token');
   window.open('https://salesapp.infinitivelogic.com/pos_user/'+token, '_blank');

  };


  const filteredProducts = products.filter(product => {
  const name        = String(product.name        ?? '').toLowerCase();
  const description = String(product.description ?? '').toLowerCase();
  const search      = searchTerm.trim().toLowerCase();

  // Force both sides to string — this fixes number vs string mismatch
  const selectedCat = String(selectedCategory ?? 'all');
  const productCat  = String(product.category ?? '');

  const matchesSearch =
    name.includes(search) ||
    productCat.toLowerCase().includes(search) ||
    description.includes(search);

  const matchesCategory =
    selectedCat === 'all' ||
    productCat === selectedCat;

  return matchesCategory && matchesSearch;
});

  // Props for CartContent
  const cartContentProps = {
    cart,
    customerInfo,
    setCustomerInfo,
    orderNote,
    setOrderNote,
    couponCode,
    setCouponCode,
    appliedCoupon,
    applyCoupon,
    removeCoupon,
    discount,
    setDiscount,
    isTaxEnabled,
    setIsTaxEnabled,
    taxRate,
    setTaxRate,
    subtotal,
    couponDiscount,
    manualDiscount,
    taxAmount,
    total,
    formatKES,
    updateQuantity,
    removeFromCart,
    saveOrder,
    printReceipt,
    setShowPayment,
    voidTransaction,
    setShowTransactionHistory,
    setShowCartModal,
    isMobile,
    shiftStatus,
    endShift,
    startShift
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {stockAlert && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-xl z-50 flex items-center space-x-2">
          <Bell className="h-5 w-5" />
          <span>{stockAlert}</span>
        </div>
      )}

      <div className="flex h-screen">
        <div className="flex-1  overflow-y-auto bg-white">
          <div className="flex bg-gradient-to-r from-blue-600 to-cyan-600 px-2 py-2 items-center justify-between mb-6 pb-4 border-b-2 border-gray-100">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-red-600 to-amber-600 text-white font-semibold rounded-xl shadow-lg">
                <Wine className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  Sales-<span className="text-white-600">App</span>
                </h1>
                <p className="text-white font-medium"></p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-white">Current Time</p>
                <p className="font-semibold text-white">{currentTime.toLocaleTimeString()}</p>
              </div>
              <button 
                onClick={() => setShowSavedOrders(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
              >
                <Clock className="h-5 w-5" />
                <span>Saved Orders</span>
                {savedOrders.length > 0 && (
                  <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs">
                    {savedOrders.length}
                  </span>
                )}
              </button>

              <button onClick={goToHome} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                Go to system
              </button>
              <button onClick={onSignOut} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                Logout
              </button>
            </div>
          </div>

          <div className="relative mb-2 p-6">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex space-x-2 mb-2 overflow-x-auto pb-2 p-6">
            {categories.map((category) => (
              <button
                key={category.id || category.name}
                onClick={() => setSelectedCategory(category.id || 'all')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all border ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
               
                <span className="font-medium">{category.name || 'Unknown'}</span>
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="text-center py-6 text-gray-500">Loading products...</div>
          ) : (
            <div className="grid p-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredProducts.length === 0 ? (
                <div className="col-span-full text-center py-6 text-gray-500">
                  No products found
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <div key={product.id || product.name} className="bg-white rounded-xl p-4 border border-gray-200 hover:border-blue-300 transition-all duration-200 hover:shadow-lg">
                    <div className="text-center mb-4">
                      <center>
                        <div className="text-4xl mb-2">
                          <img
                            src={product.image_url || product.image || 'https://via.placeholder.com/100'}
                            alt={product.name || 'Product'}
                            style={{ width: 'auto', height: '100px' }}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/100'; }}
                          />
                        </div>
                      </center>
                      <h3 className="text-md font-semibold mb-1 text-gray-800">{product.name || 'Unknown Product'}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-md font-bold text-blue-600">{formatKES(Number(product.price) || 0)}</span>
                        <span onClick={() => {
    checkStock(product);
  }} className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded cursor-pointer">
                          Stock: {Number(product.stock) || 0}
                        </span>
                      </div>
                    </div>
               <button
  onClick={() => {
    if (Number(product.stock) < 1) {
      checkStock(product);
    } else {
      addToCart(product);
    }
  }}
  className={`w-full py-2 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:cursor-not-allowed text-white
    ${Number(product.stock) < 1
      ? 'bg-gradient-to-r from-red-600 to-yellow-600 hover:from-red-700 hover:to-yellow-700'
      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
    }`}
>
  {Number(product.stock) < 1 ? 'Out of Stock - Restock' : 'Add to Cart'}
</button>


                  </div>
                ))
              )}
            </div>
          )}
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl disabled:bg-gray-300"
            >
              Previous
            </button>
            <span className="mx-4 text-gray-800">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl disabled:bg-gray-300"
            >
              Next
            </button>
          </div>
        </div>

        {!isMobile && (
          <div className="w-96 bg-white border-l-2 border-gray-100">
            <CartContent {...cartContentProps} />
          </div>
        )}
      </div>

      {isMobile && (
        <button
          onClick={() => setShowCartModal(true)}
          className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-50 md:hidden"
        >
          <ShoppingCart className="h-6 w-6" />
          {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)}
            </span>
          )}
        </button>
      )}

      {isMobile && showCartModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end justify-center z-50 md:hidden">
          <div className="bg-white rounded-t-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4">
            <CartContent {...cartContentProps} />
          </div>
        </div>
      )}

      {showSavedOrders && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Saved Orders</h3>
              <button
                onClick={() => setShowSavedOrders(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-3">
              {savedOrders.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No saved orders</p>
              ) : (
                savedOrders.map((order) => (
                  <div key={order.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-800">
                          {order.customer?.name || 'Order'} - {order.customer?.table || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(order.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">{formatKES(Number(order.order?.total_amount || 0).toFixed(2))}</p>
                        <p className="text-sm text-gray-500">{order.items?.length || 0} items</p>
                      </div>
                    </div>
                    {order.note && (
                      <p className="text-sm text-gray-600 mb-2">Note: {order.note}</p>
                    )}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => loadSavedOrder(order)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Load Order
                      </button>
                      <button
                        onClick={() => printReceipt(order)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Print Receipt
                      </button>
                      <button
                        onClick={() => deleteSavedOrder(order.id)}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {editModal && (

        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">Restock Product</h3>
          <button
            onClick={() => setEditModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form  className="space-y-4">

        <div>
            <label className="block text-sm font-medium text-gray-700">Product Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 py-2 px-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter product name"
            />
          </div>
          {/* Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Cost</label>
            <input
              type="number"
              name="cost"
              value={formData.cost}
              onChange={handleChange}
              className="mt-1 py-2 px-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter cost"
            />
          </div>

          {/* Sell Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Sell Price</label>
            <input
              type="number"
              name="sellPrice"
              value={formData.sellPrice}
              onChange={handleChange}
              className="mt-1 py-2 px-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter selling price"
            />
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Quantity</label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className="mt-1 py-2 px-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter quantity"
            />
          </div>

          {/* Multiple Image Upload */}
<div>
  <label className="block text-sm font-medium text-gray-700">Product Images</label>
  <input
    type="file"
    name="images"
    multiple
    accept="image/*"
    onChange={(e) => setFormData((prev) => ({
      ...prev,
      images: e.target.files
    }))}
    className="mt-1 py-2 px-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
  />
</div>


          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-1 py-2 px-2 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Submit */}
          <div className="pt-4">
            <button
              type="button"
              onClick={handleSubmit}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105"
            >
              Save Product
            </button>
          </div>
        </form>
      </div>
    </div>

        )}

      {showTransactionHistory && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Transaction History</h3>
              <button
                onClick={() => setShowTransactionHistory(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-3">
              {transactions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No transactions</p>
              ) : (
                transactions.map((transaction) => (
                  <div key={transaction.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-800">
                          {transaction.customer?.name || 'Order'} - {transaction.payment_method || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(transaction.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-600">{formatKES(Number(transaction.order?.total_amount || 0).toFixed(2))}</p>
                        <p className="text-sm text-gray-500">{transaction.items?.length || 0} items</p>
                        {transaction.status === 'refunded' && (
                          <p className="text-sm text-red-600">Refunded</p>
                        )}
                      </div>
                    </div>
                    {transaction.order?.note && (
                      <p className="text-sm text-gray-600 mb-2">Note: {transaction.order.note}</p>
                    )}
                    {transaction.payment_method === 'cash' && (
                      <div className="text-sm text-gray-600">
                        <p>Cash Tendered: {formatKES(Number(transaction.cash_tendered) || 0)}</p>
                        <p>Change Given: {formatKES(Number(transaction.change_due) || 0)}</p>
                      </div>
                    )}
                    <button
                      onClick={() => printReceipt(transaction)}
                      className="mt-2 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg text-sm font-medium transition-colors w-full"
                    >
                      Print Receipt
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showPayment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800">Process Payment</h3>
              <button
                onClick={() => setShowPayment(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-6">
              <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Total Amount</span>
                  <span className="text-3xl font-bold text-blue-600">{formatKES(total.toFixed(2))}</span>
                </div>
                <div className="text-sm text-gray-500">
                  Payment Method: {paymentMethod === 'card' ? 'Credit/Debit Card' : paymentMethod === 'mpesa' ? 'M-Pesa' : 'Cash'}
                </div>
                {customerInfo.name && (
                  <div className="text-sm text-gray-500 mt-1">
                    Customer: {customerInfo.name} {customerInfo.table && `(Table ${customerInfo.table})`}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <h4 className="font-semibold mb-3 text-gray-800">Payment Method</h4>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-all ${
                      paymentMethod === 'card'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <CreditCard className="h-5 w-5" />
                    <span>Card</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('mpesa')}
                    className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-all ${
                      paymentMethod === 'mpesa'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Smartphone className="h-5 w-5" />
                    <span>M-Pesa</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-all ${
                      paymentMethod === 'cash'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Wallet className="h-5 w-5" />
                    <span>Cash</span>
                  </button>
                </div>
              </div>
              {paymentMethod === 'card' ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Card Number"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={cardDetails.cardNumber || ''}
                    onChange={(e) => setCardDetails(prev => ({ ...prev, cardNumber: e.target.value }))}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="MM/YY"
                      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={cardDetails.expiry || ''}
                      onChange={(e) => setCardDetails(prev => ({ ...prev, expiry: e.target.value }))}
                    />
                    <input
                      type="text"
                      placeholder="CVV"
                      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={cardDetails.cvv || ''}
                      onChange={(e) => setCardDetails(prev => ({ ...prev, cvv: e.target.value }))}
                    />
                  </div>
                </div>
              ) : paymentMethod === 'mpesa' ? (
                <div className="space-y-3">
                  <input
                    type="tel"
                    placeholder="M-Pesa Phone Number"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={mpesaPhone || ''}
                    onChange={(e) => setMpesaPhone(e.target.value)}
                  />
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                    You will receive an M-Pesa prompt on your phone to complete the payment.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="number"
                    placeholder="Cash Tendered (Ksh)"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={cashTendered || ''}
                    onChange={(e) => handleCashTendered(e.target.value)}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    {[500, 1000, 5000].map(amount => (
                      <button
                        key={amount}
                        onClick={() => handleCashTendered(amount)}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Ksh {amount}
                      </button>
                    ))}
                  </div>
                  {cashTendered > 0 && (
                    <p className="text-sm text-gray-600">
                      Change Due: <span className="font-bold text-blue-600">{formatKES(Number(changeDue) || 0)}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={processPayment}
              disabled={isProcessing || (paymentMethod === 'cash' && (Number(cashTendered) < total || isNaN(Number(cashTendered))))}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 text-white py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Check className="h-5 w-5" />
                  <span>Complete Payment</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubPOS;